import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { generateAuthHeader } from "../../../commonfunctions/authentication";
import { log } from "../../../commonfunctions/logger";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import SectionCard from "../../../components/SectionCard/SectionCard";
import Clipboard from "../../../assets/clipboard";

interface CustomerInfoAPIResponse {
  allow_webstore: boolean;
  brands: string[];
  company: string;
  company_accpac_no: string;
  customer_branch: string;
  customer_id: number;
  email: string;
  first_name: string;
  last_name: string;
}

const CustomerInfo = () => {
  const [customerInfo, setCustomerInfo] = useState<CustomerInfoAPIResponse | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const getCustomerInfo = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_HALFBACK_ROOT_API}/current_customer/info`,
          generateAuthHeader(),
        );

        if (res.ok) {
          const data = await res.json();
          setCustomerInfo(data);
        } else {
          const errorText = await res.text();
          throw new Error(errorText || "Failed to fetch customer info");
        }
      } catch (error) {
        log(error, "Failed to fetch customer info at /preferences");
        setError("Unable to load your information. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    getCustomerInfo();
  }, [navigate]);

  if (isLoading) {
    return (
      <SectionCard title="Your Information" icon={<Clipboard width={20} height={20} />}>
        <LoadingSpinner block />
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard title="Your Information" icon={<Clipboard width={20} height={20} />}>
        <div className="preferences-error-state">
          <p className="error-message">{error}</p>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Your Information" icon={<Clipboard width={20} height={20} />}>
      {customerInfo ? (
        <div className="customer-info-grid">
          <div className="info-field">
            <label className="info-label">Name</label>
            <span className="info-value">{`${customerInfo.first_name} ${customerInfo.last_name}`}</span>
          </div>
          <div className="info-field">
            <label className="info-label">Email</label>
            <span className="info-value">{customerInfo.email ?? "-"}</span>
          </div>
          <div className="info-field">
            <label className="info-label">Company</label>
            <span className="info-value">{customerInfo.company ?? "-"}</span>
          </div>
          <div className="info-field">
            <label className="info-label">Company ID</label>
            <span className="info-value">{customerInfo.company_accpac_no ?? "-"}</span>
          </div>
          <div className="info-field">
            <label className="info-label">Country</label>
            <span className="info-value">{customerInfo.customer_branch.toUpperCase() ?? "-"}</span>
          </div>
          <div className="info-field info-field-full">
            <label className="info-label">Available Brands</label>
            <div className="brands-list">
              {customerInfo.brands && customerInfo.brands.length > 0 ? (
                customerInfo.brands.map((brand, index) => (
                  <span key={index} className="brand-tag">
                    {brand}
                  </span>
                ))
              ) : (
                <span className="info-value">No brands available</span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="preferences-empty-state">
          <p>No information available</p>
        </div>
      )}
    </SectionCard>
  );
};

export default CustomerInfo;
